"use client";

import { IntentBadge, SettingsSection, formatRelativeTime } from "@excellent/ui";
import type { BenchmarkPayload } from "@excellent/ui/api/contracts/models-config";

// The benchmark evidence trail (docs/models/PRD.md §13/§14): the probe's
// eligibility map + sweep verdicts, rendered so a promotion's evidence is
// visible — which verdict cleared which model for which job class, and when.

export function BenchmarkSection({ benchmark }: { benchmark: BenchmarkPayload | null }) {
  return (
    <SettingsSection
      title="Benchmark evidence"
      description="Per job class, which models cleared the gate and at what cost — the premise probe's eligibility map plus continuous sweep verdicts. A model routes ONLY after its promotion lands through Crucible governance."
    >
      {!benchmark || benchmark.rows.length === 0 ? (
        <p className="type-body text-muted-foreground">
          No sweep runs yet. Run <code className="type-subtext">excellent-mcp probe run --live</code> to
          measure real models, or the continuous sweep once routing is enabled.
        </p>
      ) : (
        <div>
          <p className="mb-2 type-subtext text-muted-foreground">
            Source: {benchmark.source}
            {benchmark.generatedAt ? <> · generated {formatRelativeTime(benchmark.generatedAt)}</> : null}
          </p>
          {benchmark.rows.map((row, i) => (
            <div
              key={`${row.jobClass}-${row.model}-${i}`}
              className="flex items-center gap-3 border-b border-border/40 py-2 first:pt-0 last:border-b-0"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="type-body text-foreground">{row.jobClass}</span>
                  <span className="truncate type-subtext text-muted-foreground">
                    {row.provider ? `${row.provider} · ` : ""}
                    {row.model}
                  </span>
                  {row.cheapestPassing ? <IntentBadge intent="success">cheapest passing</IntentBadge> : null}
                </div>
                {row.note ? <p className="truncate type-subtext text-muted-foreground">{row.note}</p> : null}
              </div>
              <span className="shrink-0 type-subtext text-muted-foreground">
                {row.passRate != null ? `pass ${(row.passRate * 100).toFixed(0)}%` : "—"}
                {row.silentWrong != null ? ` · silent-wrong ${(row.silentWrong * 100).toFixed(0)}%` : ""}
              </span>
            </div>
          ))}
        </div>
      )}
    </SettingsSection>
  );
}
